import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchUser, startGoogleLogin } from "@/lib/userApi";

const NAV = [
  { label: "Atlas", to: "/" },
  { label: "Dashboard", to: "/dashboard" },
  { label: "Clips", to: "/clips" },
  { label: "Signals", to: "/signals" },
  { label: "Higher / Lower", to: "/game" },
  { label: "Store", to: "/store" },
  { label: "Premium", to: "/premium" },
  { label: "Sponsor", to: "/sponsor" },
];

export const Header = () => {
  const loc = useLocation();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      const u = await fetchUser();
      setUser(u);
      setReady(true);
    })();
  }, [loc.pathname]);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white border-b border-black"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <span className="w-3 h-3 bg-[#ff3b30] inline-block" />
          <span className="font-display font-extrabold text-xl tracking-tighter">
            UNIVERSTREND
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const active = loc.pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`overline px-4 py-2 border ${
                  active
                    ? "bg-black text-white border-black"
                    : "border-transparent hover:border-black"
                }`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop auth */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link
              to="/me"
              data-testid="header-user-link"
              className="overline px-3 py-2 border border-black hover:bg-black hover:text-white flex items-center gap-2"
              title={user.email}
            >
              <span className="w-2 h-2 bg-[#ff3b30] inline-block" />
              {user.name.split(" ")[0]}
            </Link>
          ) : (
            <button
              data-testid="header-signin-btn"
              onClick={startGoogleLogin}
              className={`overline px-3 py-2 border border-black hover:bg-black hover:text-white transition ${ready ? "" : "opacity-40"}`}
            >
              Sign in
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-10 h-10 border border-black flex flex-col items-center justify-center gap-1.5"
        >
          <span
            className={`block w-5 h-[2px] bg-black transition-transform ${menuOpen ? "translate-y-[7px] rotate-45" : ""}`}
          />
          <span
            className={`block w-5 h-[2px] bg-black transition-opacity ${menuOpen ? "opacity-0" : ""}`}
          />
          <span
            className={`block w-5 h-[2px] bg-black transition-transform ${menuOpen ? "-translate-y-[7px] -rotate-45" : ""}`}
          />
        </button>
      </div>

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-white z-40 overflow-y-auto border-t border-black"
        >
          <nav className="flex flex-col">
            {NAV.map((n) => {
              const active = loc.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setMenuOpen(false)}
                  className={`overline px-6 py-5 border-b border-black text-base ${
                    active ? "bg-black text-white" : "bg-white hover:bg-[#f4f4f5]"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
            <div className="px-6 py-6 border-b border-black bg-[#fafafa]">
              {user ? (
                <Link
                  to="/me"
                  data-testid="mobile-user-link"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 overline"
                >
                  <span className="w-2.5 h-2.5 bg-[#ff3b30] inline-block" />
                  <span>Signed in as {user.name.split(" ")[0]}</span>
                  <span className="ml-auto">→</span>
                </Link>
              ) : (
                <button
                  data-testid="mobile-signin-btn"
                  onClick={() => { setMenuOpen(false); startGoogleLogin(); }}
                  className="w-full hard-shadow border border-black bg-black text-white px-4 py-3 overline"
                >
                  Sign in with Google
                </button>
              )}
            </div>
            <div className="px-6 py-5 font-mono text-[11px] uppercase tracking-widest text-[#71717a]">
              Universtrend · vol. 01
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
